import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';

import { NgIf } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { MixpanelService } from '@lib/mixpanel';
import { t, TranslationModule } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent, SnDataListModule } from '@lib/ui/data-list';

import { LprExport, LprExportServiceAPI } from '../../api';
import { AlprSearchExportsTableComponent } from '../../components/alpr-search-exports-table/alpr-search-exports-table.component';
import { LprExportStatus } from '../../models/alpr-export-status.enum';
import { ViDownloadService } from '../../shared/services/vi-download.service';

@Component({
  selector: 'fs-alpr-search-exports',
  templateUrl: './alpr-search-exports.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AlprSearchExportsTableComponent, SnDataListModule, NgIf, TranslationModule],
})
export class AlprSearchExportsComponent implements OnDestroy {
  @ViewChild('listElement', { static: true })
  public readonly list: SnDataListComponent<LprExport>;
  public readonly loadData = this._loadData.bind(this);
  public readonly actions: SnActionConfig<LprExport>[] = this._getActions();
  public readonly columns: string[] = this._getColumns();
  public sort = 'startedAt';
  public sortDirection: SortDirection = 'desc';

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _lprExportService: LprExportServiceAPI,
    private readonly _viDownloadService: ViDownloadService,
    private readonly _toastrService: ToastrService,
    private readonly _mixpanelService: MixpanelService,
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;

    this.list.reference.reload();
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<LprExport>> {
    return this._lprExportService.getExports(this._prepareLoadQueryParameters(params)).pipe(
      catchError((err) => {
        this._toastrService.error(t('alpr.export.errors.getExportsAPIError'));
        return EMPTY;
      }),
      takeUntil(this._destroy$),
    );
  }

  private _prepareLoadQueryParameters(params: ISnListQueryParameters): ISnListQueryParameters {
    return {
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      ...params,
    };
  }

  private _getColumns(): string[] {
    return [
      'searchReason',
      'startedAt',
      'completedAt',
      'processingTime',
      'status',
      'expires',
      'fileSize',
      'actions',
    ];
  }

  private _getActions(): SnActionConfig<LprExport>[] {
    return [
      {
        label: t('common.download'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-file-download',
        },
        click: (lprExport) => this._downloadMedia(lprExport),
        hidden: (lprExport) => this._isDownloadExportHidden(lprExport),
        disabled: (lprExport) => this._isDownloadExportDisabled(lprExport),
      },
      {
        label: t('common.retry'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-arrow-u-turn-up-left',
        },
        click: (lprExport) => this._retryReport(lprExport),
        hidden: (lprExport) => this._isRetryExportHidden(lprExport),
      },
    ];
  }

  private _isDownloadExportHidden(lprExport: LprExport): boolean {
    return lprExport.status === LprExportStatus.FAILED;
  }

  private _isDownloadExportDisabled(lprExport: LprExport): boolean {
    const expires = new Date(lprExport.startedAt);
    expires.setDate(expires.getDate() + 7);
    const now = new Date();

    return (
      lprExport.status === LprExportStatus.IN_PROGRESS ||
      lprExport.status === LprExportStatus.PENDING ||
      expires <= now
    );
  }

  private _downloadMedia(lprExport: LprExport): void {
    if (!lprExport.report) {
      this._toastrService.error(t('alpr.errors.mediaUrlMissing'));
      return;
    }

    if (lprExport.status === LprExportStatus.COMPLETE) {
      this._viDownloadService
        .downloadFile(lprExport.report)
        .pipe(
          map((response) => response?.body),
          filter((blob): blob is Blob => !!blob),
          tap((file: Blob) => {
            const fileExtension = file.type;
            const fileName = `${lprExport.params.searchReason}-${Date.now()}.${fileExtension}`;
            saveAs(file, fileName);
          }),
          tap(() => {
            this._mixpanelService.trackMetric(MxEventName.DownloadLprReportAf, {
              [MxPropertyName.FileType]: lprExport.reportFormat,
            });
          }),
          catchError((err) => {
            this._toastrService.error(t('alpr.errors.downloadMediaFailed'));
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  private _isRetryExportHidden(lprExport: LprExport): boolean {
    return lprExport.status !== LprExportStatus.FAILED;
  }

  private _retryReport(lprExport: LprExport) {
    this._lprExportService
      .retryExport(lprExport.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (lprExportResponse) => {
          if (lprExportResponse) {
            this._toastrService.success(t('alpr.export.messages.retry'));
            this.list.dataSource.updateItem(lprExportResponse);
          }
        },
        error: () => {
          this._toastrService.error(t('alpr.export.errors.retryExportsAPIError'));
        },
      });
  }
}
